import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Link,
  Alert,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  DeleteForever as DeleteIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '../LanguageSelector';

const DeleteAccount = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc'
    }}>
      {/* AppBar for consistency with main app */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#1e293b'
            }}
          >
            SoldiKeeper
          </Typography>
          <LanguageSelector />
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff'
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <DeleteIcon 
              sx={{ 
                fontSize: 60, 
                color: 'error.main',
                mb: 2 
              }} 
            />
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                color: theme.palette.mode === 'dark' ? '#ffffff' : '#1e293b'
              }}
            >
              {t('deleteAccount.title', 'Account Deletion')}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              {t('deleteAccount.subtitle', 'Request to delete your SoldiKeeper account and data')}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Important Notice */}
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('deleteAccount.warning', 'Account deletion is permanent and cannot be undone.')}
            </Typography>
          </Alert>

          {/* How to Delete Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? '#ffffff' : '#1e293b'
              }}
            >
              {t('deleteAccount.howToDelete', 'How to Delete Your Account')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('deleteAccount.methods', 'You can delete your account using one of the following methods:')}
            </Typography>

            <List sx={{ mt: 2 }}>
              <ListItem sx={{ alignItems: 'flex-start' }}>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.method1Title', 'In-App Deletion (Recommended)')}
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {t('deleteAccount.method1Description', 
                        'Navigate to Settings > Account > Delete Account within the SoldiKeeper app. Follow the prompts to confirm deletion.'
                      )}
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ alignItems: 'flex-start' }}>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.method2Title', 'Email Request')}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {t('deleteAccount.method2Description', 
                          'Send an email to our support team with your account deletion request:'
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('deleteAccount.emailLabel', 'Email:')} {' '}
                        <Link 
                          href="mailto:privacy@soldikeeper.com?subject=Account Deletion Request"
                          sx={{ 
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          privacy@soldikeeper.com
                        </Link>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('deleteAccount.emailInstructions', 
                          'Please include your registered email address and any details to help us identify your account.'
                        )}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* What Gets Deleted Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? '#ffffff' : '#1e293b'
              }}
            >
              {t('deleteAccount.whatDeleted', 'What Gets Deleted')}
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <StorageIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.dataItem1', 'All personal information and profile data')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <StorageIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.dataItem2', 'All financial transactions and budget data')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <StorageIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.dataItem3', 'All receipts and uploaded documents')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <StorageIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.dataItem4', 'All split bills and group transactions')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <StorageIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.dataItem5', 'AI insights and personalized recommendations')}
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Timeline Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? '#ffffff' : '#1e293b'
              }}
            >
              {t('deleteAccount.timeline', 'Deletion Timeline')}
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <ScheduleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.timelineImmediate', 'Immediate: Account access is revoked')}
                  secondary={t('deleteAccount.timelineImmediateDesc', 
                    'You will be logged out and unable to access your account.'
                  )}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ScheduleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('deleteAccount.timeline30Days', 'Within 30 days: Complete data deletion')}
                  secondary={t('deleteAccount.timeline30DaysDesc', 
                    'All your personal data will be permanently deleted from our servers.'
                  )}
                />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {t('deleteAccount.retentionNotice', 
                  'Some information may be retained for legal or security purposes as outlined in our Privacy Policy.'
                )}
              </Typography>
            </Alert>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Contact Information */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('deleteAccount.questionsText', 'Have questions about account deletion?')}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EmailIcon />}
              href="mailto:privacy@soldikeeper.com"
              sx={{ mb: 2 }}
            >
              {t('deleteAccount.contactSupport', 'Contact Support')}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {t('deleteAccount.privacyPolicyText', 'For more information, read our')}{' '}
              <Link 
                href="/privacy-policy"
                sx={{ 
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {t('deleteAccount.privacyPolicyLink', 'Privacy Policy')}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DeleteAccount;
